
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Package,
  ShoppingCart,
  Users,
  TrendingUp,
  Plus,
  Trash2,
  Eye,
  BarChart2,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  LoaderCircle,
  AlertCircle,
  X,
  CheckCircle,
} from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import { useProducts } from "../../context/ProductContext";
import { orderService } from "../../services/orderService";

const COLORS = {
  purple: {
    bg: "bg-violet-50",
    text: "text-violet-600",
    border: "border-violet-100",
    hex: "#7c3aed",
  },
  green: {
    bg: "bg-emerald-50",
    text: "text-emerald-600",
    border: "border-emerald-100",
    hex: "#059669",
  },
  amber: {
    bg: "bg-amber-50",
    text: "text-amber-600",
    border: "border-amber-100",
    hex: "#d97706",
  },
  blue: {
    bg: "bg-blue-50",
    text: "text-blue-600",
    border: "border-blue-100",
    hex: "#2563eb",
  },
  red: {
    bg: "bg-red-50",
    text: "text-red-600",
    border: "border-red-100",
    hex: "#dc2626",
  },
};

const STATUS_MAP = {
  pending: {
    label: "En attente",
    style: "bg-amber-50 text-amber-700 ring-amber-200",
  },
  confirmed: {
    label: "Confirmée",
    style: "bg-blue-50 text-blue-700 ring-blue-200",
  },
  shipped: {
    label: "Expédiée",
    style: "bg-violet-50 text-violet-700 ring-violet-200",
  },
  delivered: {
    label: "Livrée",
    style: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  },
  cancelled: {
    label: "Annulée",
    style: "bg-red-50 text-red-700 ring-red-200",
  },
};

const CATEGORY_CONFIG = [
  {
    label: "Beauté",
    values: ["Beauté", "Beauty"],
    color: "purple",
  },
  {
    label: "Électronique",
    values: ["Électronique", "Electronique", "Électroniques"],
    color: "blue",
  },
  {
    label: "Sports",
    values: ["Sports", "Sport"],
    color: "green",
  },
  {
    label: "Coffrets",
    values: ["Coffrets", "Coffret"],
    color: "amber",
  },
];

const FALLBACK_IMAGE =
  "https://placehold.co/100x100/f5f3f0/78716c?text=Produit";

const formatPrice = (value) =>
  `${Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} MAD`;

const formatDate = (date) => {
  if (!date) return "—";

  try {
    const parsedDate =
      typeof date?.toDate === "function"
        ? date.toDate()
        : new Date(date);

    if (Number.isNaN(parsedDate.getTime())) return "—";

    return parsedDate.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

function StatCard({ icon: Icon, label, value, color }) {
  const c = COLORS[color] || COLORS.purple;

  return (
    <div className="group rounded-2xl border border-stone-200/80 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-md sm:p-6">
      <div className="mb-5 flex items-start justify-between">
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-xl ${c.bg}`}
        >
          <Icon size={23} className={c.text} />
        </div>

        <div className="rounded-lg bg-stone-50 p-2 text-stone-400 transition group-hover:text-stone-600">
          <ArrowUpRight size={17} />
        </div>
      </div>

      <p className="text-sm font-medium text-stone-500">{label}</p>

      <h3 className="mt-2 break-words text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl">
        {value}
      </h3>
    </div>
  );
}

function StatusBadge({ status }) {
  const normalizedStatus = STATUS_MAP[status]
    ? status
    : "pending";

  const item = STATUS_MAP[normalizedStatus];

  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${item.style}`}
    >
      {item.label}
    </span>
  );
}

function SectionHeader({ icon: Icon, title, to, linkLabel }) {
  return (
    <div className="mb-5 flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-2.5 text-base font-bold text-stone-900 sm:text-lg">
        <Icon size={21} className="text-violet-600" />
        {title}
      </h2>

      {to && (
        <Link
          to={to}
          className="shrink-0 rounded-lg bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-700 transition hover:bg-violet-100 sm:text-sm"
        >
          {linkLabel}
        </Link>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();

  const {
    products = [],
    fetchProducts,
    removeProduct,
  } = useProducts();

  const [orders, setOrders] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loadOrders, setLoadOrders] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [productActionError, setProductActionError] = useState("");
  const [productActionSuccess, setProductActionSuccess] = useState("");
  const [deletingProductId, setDeletingProductId] = useState(null);

  const loadOrderData = useCallback(async () => {
    setLoadOrders(true);
    setLoadError("");

    try {
      const [allOrders, latestOrders] = await Promise.all([
        orderService.getAll(),
        orderService.getRecentOrders(),
      ]);

      const safeOrders = Array.isArray(allOrders) ? allOrders : [];
      const safeRecentOrders = Array.isArray(latestOrders)
        ? latestOrders
        : [];

      setOrders(safeOrders);
      setRecentOrders(safeRecentOrders);
    } catch (err) {
      console.error("Erreur de chargement des commandes :", err);
      setLoadError("Impossible de charger les commandes.");
    } finally {
      setLoadOrders(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    loadOrderData();
  }, [fetchProducts, loadOrderData]);

  const safeProducts = Array.isArray(products) ? products : [];

  const totalRevenue = orders.reduce(
    (sum, order) => sum + Number(order.total || 0),
    0
  );

  const totalClients = new Set(
    orders.map((order) => order.userId).filter(Boolean)
  ).size;

  const stats = [
    {
      icon: TrendingUp,
      label: "Chiffre d'affaires",
      value: formatPrice(totalRevenue),
      color: "purple",
    },
    {
      icon: ShoppingCart,
      label: "Commandes",
      value: orders.length.toLocaleString("fr-FR"),
      color: "blue",
    },
    {
      icon: Users,
      label: "Clients",
      value: totalClients.toLocaleString("fr-FR"),
      color: "green",
    },
    {
      icon: Package,
      label: "Produits",
      value: safeProducts.length.toLocaleString("fr-FR"),
      color: "amber",
    },
  ];

  const recentProducts = safeProducts.slice(0, 5);

  const handleDeleteProduct = async (product) => {
    const confirmed = window.confirm(
      `Voulez-vous vraiment supprimer le produit « ${product.name} » ?`
    );

    if (!confirmed) return;

    if (typeof removeProduct !== "function") {
      setProductActionError(
        "La fonction de suppression n'est pas disponible dans ProductContext."
      );
      return;
    }

    setDeletingProductId(product.id);
    setProductActionError("");
    setProductActionSuccess("");

    try {
      await removeProduct(product.id);
      setProductActionSuccess("Produit supprimé avec succès.");
    } catch (err) {
      console.error("Erreur de suppression :", err);
      setProductActionError(
        err.message || "Impossible de supprimer ce produit."
      );
    } finally {
      setDeletingProductId(null);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <div className="mx-auto max-w-[1440px] space-y-8">

        {/* En-tête */}
        <header className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-violet-600">
              Espace administration
            </p>

            <h1 className="text-2xl font-bold tracking-tight text-stone-900 sm:text-3xl">
              Dashboard Admin
            </h1>

            <p className="mt-2 text-sm text-stone-500">
              Bienvenue,{" "}
              <span className="font-semibold text-stone-800">
                {user?.displayName || user?.email || "Administrateur"}
              </span>
            </p>
          </div>

          <div className="flex flex-col gap-3 min-[480px]:flex-row">
            <Link
              to="/admin/product/add"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
            >
              <Plus size={18} />
              Ajouter un produit
            </Link>

            <Link
              to="/admin/orders"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm font-semibold text-stone-700 transition hover:bg-stone-100 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
            >
              <ShoppingCart size={18} />
              Voir les commandes
            </Link>
          </div>
        </header>

        {/* Messages */}
        {loadError && (
          <div
            role="alert"
            className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
          >
            <AlertCircle size={19} className="shrink-0" />
            <p className="flex-1">{loadError}</p>

            <button
              type="button"
              onClick={() => setLoadError("")}
              aria-label="Fermer"
              className="rounded-lg p-1 hover:bg-red-100"
            >
              <X size={17} />
            </button>
          </div>
        )}

        {productActionError && (
          <div
            role="alert"
            className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
          >
            <AlertCircle size={19} className="shrink-0" />
            <p className="flex-1">{productActionError}</p>
            <button
              type="button"
              onClick={() => setProductActionError("")}
              aria-label="Fermer"
              className="rounded-lg p-1 hover:bg-red-100"
            >
              <X size={17} />
            </button>
          </div>
        )}

        {productActionSuccess && (
          <div
            role="status"
            className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700"
          >
            <CheckCircle size={19} className="shrink-0" />
            <p className="flex-1">{productActionSuccess}</p>
            <button
              type="button"
              onClick={() => setProductActionSuccess("")}
              aria-label="Fermer"
              className="rounded-lg p-1 hover:bg-emerald-100"
            >
              <X size={17} />
            </button>
          </div>
        )}

        {/* Statistiques */}
        <section>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-500">
              Vue d'ensemble
            </h2>

            {loadOrders && (
              <span className="inline-flex items-center gap-2 text-xs text-stone-400">
                <LoaderCircle
                  size={14}
                  className="animate-spin text-violet-600"
                />
                Actualisation...
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 min-[480px]:grid-cols-2 xl:grid-cols-4">
            {stats.map((stat) => (
              <StatCard key={stat.label} {...stat} />
            ))}
          </div>
        </section>

        {/* Commandes et produits récents */}
        <div className="grid grid-cols-1 items-stretch gap-6 xl:grid-cols-12">

          {/* Commandes récentes */}
          <section className="min-w-0 rounded-2xl border border-stone-200/80 bg-white p-4 shadow-sm sm:p-6 xl:col-span-7">
            <SectionHeader
              icon={Clock}
              title="Commandes récentes"
              to="/admin/orders"
              linkLabel="Voir toutes →"
            />

            {loadOrders ? (
              <div className="flex min-h-64 flex-col items-center justify-center gap-3 text-sm text-stone-500">
                <LoaderCircle
                  size={28}
                  className="animate-spin text-violet-600"
                />
                Chargement des commandes...
              </div>
            ) : recentOrders.length === 0 ? (
              <div className="flex min-h-64 flex-col items-center justify-center text-center">
                <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-stone-100">
                  <ShoppingCart size={25} className="text-stone-400" />
                </div>
                <p className="font-semibold text-stone-700">
                  Aucune commande trouvée
                </p>
                <p className="mt-1 text-sm text-stone-400">
                  Les nouvelles commandes apparaîtront ici.
                </p>
              </div>
            ) : (
              <div className="-mx-4 overflow-x-auto sm:-mx-6">
                <table className="w-full min-w-[680px] border-collapse text-left">
                  <thead>
                    <tr className="border-y border-stone-100 bg-stone-50/70 text-xs text-stone-500">
                      <th className="px-4 py-3 font-semibold sm:px-6">
                        Client
                      </th>
                      <th className="px-4 py-3 font-semibold">Articles</th>
                      <th className="px-4 py-3 font-semibold">Total</th>
                      <th className="px-4 py-3 font-semibold">Statut</th>
                      <th className="px-4 py-3 font-semibold sm:pr-6">
                        Date
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-stone-100">
                    {recentOrders.slice(0, 5).map((order) => {
                      const firstName =
                        order.shipping?.firstName || "";
                      const lastName =
                        order.shipping?.lastName || "";
                      const fullName =
                        `${firstName} ${lastName}`.trim() || "Client";

                      const initials =
                        `${firstName.charAt(0)}${lastName.charAt(0)}`
                          .toUpperCase() || "C";

                      return (
                        <tr
                          key={order.id}
                          className="transition hover:bg-stone-50/70"
                        >
                          <td className="px-4 py-4 sm:px-6">
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-100 text-xs font-bold text-violet-700">
                                {initials}
                              </div>

                              <div className="min-w-0">
                                <p className="max-w-40 truncate text-sm font-semibold text-stone-800">
                                  {fullName}
                                </p>
                                <p className="mt-1 text-xs text-stone-400">
                                  {order.userId
                                    ? `ID : ${order.userId.slice(0, 8)}…`
                                    : "Client"}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-4">
                            <span className="inline-flex min-w-7 items-center justify-center rounded-lg bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
                              {Array.isArray(order.items)
                                ? order.items.reduce(
                                    (sum, item) =>
                                      sum + Number(item.quantity || 1),
                                    0
                                  )
                                : 0}
                            </span>
                          </td>

                          <td className="whitespace-nowrap px-4 py-4 text-sm font-bold text-violet-700">
                            {formatPrice(order.total)}
                          </td>

                          <td className="px-4 py-4">
                            <StatusBadge status={order.status} />
                          </td>

                          <td className="whitespace-nowrap px-4 py-4 text-xs text-stone-500 sm:pr-6">
                            {formatDate(order.createdAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Produits récents */}
          <section className="min-w-0 rounded-2xl border border-stone-200/80 bg-white p-4 shadow-sm sm:p-6 xl:col-span-5">
            <SectionHeader
              icon={Package}
              title="Produits récents"
              to="/admin/product"
              linkLabel="Tous →"
            />

            {recentProducts.length === 0 ? (
              <div className="flex min-h-52 flex-col items-center justify-center text-center">
                <Package size={30} className="mb-3 text-stone-300" />
                <p className="text-sm font-semibold text-stone-700">
                  Aucun produit disponible
                </p>
                <p className="mt-1 text-xs text-stone-400">
                  Ajoute des produits pour les voir ici.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentProducts.map((product) => (
                  <div
                    key={product.id}
                    className="flex min-w-0 items-center gap-3 rounded-xl border border-stone-100 bg-stone-50/70 p-3 transition hover:border-stone-200 hover:bg-white"
                  >
                    <img
                      src={
                        product.mainImage ||
                        product.image ||
                        FALLBACK_IMAGE
                      }
                      alt={product.name || "Produit"}
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = FALLBACK_IMAGE;
                      }}
                      className="h-12 w-12 shrink-0 rounded-xl border border-stone-200 object-cover"
                    />

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-stone-800">
                        {product.name || "Produit sans nom"}
                      </p>

                      <p className="mt-1 text-xs font-semibold text-violet-700">
                        {formatPrice(product.price)}
                      </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-1.5">
                      <Link
                        to={`/admin/product/edit/${product.id}`}
                        aria-label={`Voir ou modifier ${product.name}`}
                        title="Modifier le produit"
                        className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600 transition hover:bg-blue-100"
                      >
                        <Eye size={16} />
                      </Link>

                      <button
                        type="button"
                        onClick={() => handleDeleteProduct(product)}
                        disabled={deletingProductId === product.id}
                        aria-label={`Supprimer ${product.name}`}
                        title="Supprimer le produit"
                        className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {deletingProductId === product.id ? (
                          <LoaderCircle
                            size={16}
                            className="animate-spin"
                          />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Link
              to="/admin/product/add"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-semibold text-violet-700 transition hover:border-violet-300 hover:bg-violet-100"
            >
              <Plus size={17} />
              Ajouter un produit
            </Link>
          </section>
        </div>

        {/* Produits par catégorie */}
        <section className="rounded-2xl border border-stone-200/80 bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50">
              <BarChart2 size={21} className="text-violet-600" />
            </div>

            <div>
              <h2 className="text-base font-bold text-stone-900 sm:text-lg">
                Produits par catégorie
              </h2>
              <p className="mt-1 text-xs text-stone-500">
                Répartition de ton catalogue actuel
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 min-[480px]:grid-cols-2 xl:grid-cols-4">
            {CATEGORY_CONFIG.map((category) => {
              const count = safeProducts.filter((product) =>
                category.values.some(
                  (value) =>
                    String(product.category || "").toLowerCase() ===
                    value.toLowerCase()
                )
              ).length;

              const percentage = safeProducts.length
                ? Math.round((count / safeProducts.length) * 100)
                : 0;

              const color = COLORS[category.color];

              return (
                <div
                  key={category.label}
                  className={`rounded-2xl border ${color.border} ${color.bg} p-5 transition hover:shadow-sm`}
                >
                  <p className={`text-sm font-semibold ${color.text}`}>
                    {category.label}
                  </p>

                  <div className="mt-3 flex items-end justify-between gap-2">
                    <h3 className="text-3xl font-bold tracking-tight text-stone-900">
                      {count}
                    </h3>

                    <span className="text-xs font-semibold text-stone-500">
                      {percentage}%
                    </span>
                  </div>

                  <div
                    className="mt-4 h-2 overflow-hidden rounded-full bg-white/80"
                    role="progressbar"
                    aria-label={`${category.label} : ${percentage}% des produits`}
                    aria-valuenow={percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: color.hex,
                      }}
                    />
                  </div>

                  <p className="mt-2 text-xs text-stone-500">
                    du catalogue total
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
